import { useEffect } from 'react';
import { AnnotationIcon } from '@heroicons/react/outline';
import { useSetAtom, useAtom } from 'jotai';
import { notificationAtom, pageLoadingAtom } from '../../atoms/system';
import { popupReplyAtom, listLoadReplyAtom } from '../../atoms/reply';
import api from '../../api';
import _ from 'lodash';
import cuid from 'cuid';
import {
  IResponse,
  CommentStatus,
  SortBy,
  TargetItem,
  ActionType,
} from '../../types';
import {
  Header,
  DeleteConfirm as ListReplyQuestionDeleteConfirm,
} from '../../widgets';
import ReactPaginate from 'react-paginate';
import { useImmer } from 'use-immer';
import queryString from 'query-string';
import ItemReply from './Item.Reply';
import PopupQuestionReply from '../Reply/Popup.Reply';
import { LISTURL } from '../../config';
import produce from 'immer';
import clsx from 'clsx';
import { IReply } from '../../inter/list';
import { ListReplyPayload } from '../../schema';

const breadcrumbs = [
  { name: 'Câu hỏi', href: LISTURL.question_list, current: false },
  {
    name: 'Danh sách trả lời câu hỏi',
    href: LISTURL.question_reply,
    current: true,
  },
];

const List = (): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const pageLoading = useSetAtom(pageLoadingAtom);
  const [listLoad, setListLoad] = useAtom(listLoadReplyAtom);
  const [listData, setListData] = useImmer<IReply>({
    limit: 50,
    list: [],
    page: 1,
    total: -1,
  });
  const [listTarget, setListTarget] = useImmer<{ [key: string]: TargetItem }>(
    {},
  );
  const [queryOptions, setQueryOptions] = useImmer<ListReplyPayload>({
    page: listData.page,
    limit: listData.limit,
    sort_by: SortBy.createdDesc,
    status: null,
  });
  useEffect(() => {
    (async () => {
      try {
        pageLoading(true);
        const query = queryString.stringify(queryOptions, {
          skipNull: true,
          skipEmptyString: true,
        });
        const result = await api
          .get(`api/replies/question?${query}`)
          .json<IResponse<IReply>>();
        const targetItems: { [key: string]: TargetItem } = {};
        if (result.data.list.length > 0) {
          const targetIds = _.map(result.data.list, v => v.pageTargetId);
          const targetSearch = await api
            .post('api/search/product', {
              json: {
                q: JSON.stringify(targetIds),
              },
            })
            .json<IResponse<TargetItem[]>>();
          _.forEach(targetSearch.data, v => {
            targetItems[v.product_id] = v;
          });
        }
        setListTarget(targetItems);
        setListData(result.data);
      } catch (e) {
        console.log(e);
        notification({
          show: true,
          type: null,
          title: null,
        });
      } finally {
        pageLoading(false);
      }
    })();
  }, [listLoad]);

  const setShowPopupRely = useSetAtom(popupReplyAtom);
  const [showDeleteItem, setShowDeleteItem] = useImmer<{
    show: boolean;
    comment_id: string;
  }>({
    show: false,
    comment_id: '',
  });
  const handleDeleteItem = async (comment_id: string) => {
    try {
      pageLoading(true);
      await api.post(`api/comments/${comment_id}/delete-question`);
      setShowDeleteItem(draft => {
        draft.show = false;
        draft.comment_id = '';
      });
      setListLoad(cuid());
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    } finally {
      pageLoading(false);
    }
  };
  const handleAction = async (
    type: ActionType,
    item: {
      id: string;
      parent_id?: string;
      target?: Omit<TargetItem, '_id' | 'product_id'>;
    },
  ) => {
    if (type === ActionType.reply) {
      setShowPopupRely(
        produce(draft => {
          if (_.isString(item.parent_id) && item.parent_id.length > 0) {
            draft.show = true;
            draft.reply_id = item.id;
            draft.parent_id = item.parent_id;
            if (!_.isUndefined(item.target)) {
              draft.target.title = item.target.title;
              draft.target.handle = item.target.handle;
            }
          }
        }),
      );
    } else if (type === ActionType.delete) {
      setShowDeleteItem(draft => {
        draft.show = true;
        draft.comment_id = item.id;
      });
    } else if (type === ActionType.approve) {
      try {
        pageLoading(true);
        await api.post(`api/comments/${item.id}/approve`);
        setListLoad(cuid());
      } catch (e) {
        console.log(e);
        notification({
          show: true,
          type: null,
          title: null,
        });
      } finally {
        pageLoading(false);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Header
          title="Danh sách trả lời câu hỏi"
          breadcrumbs={breadcrumbs}
          action={null}
        />
        <div className="p-3 mt-3">
          <div className="flex flex-col space-y-4">
            <div className="flex space-x-8 text-md text-gray-600 font-medium">
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = null;
                  });
                  setListLoad(cuid());
                }}
                className={clsx(
                  queryOptions.status === null
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Tất cả trả lời
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.pending;
                  });
                  setListLoad(cuid());
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.pending
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Trả lời chờ duyệt
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.active;
                  });
                  setListLoad(cuid());
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.active
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Trả lời đã duyệt
              </button>
              <button
                type="button"
                onClick={() => {
                  setQueryOptions(draft => {
                    draft.status = CommentStatus.inactive;
                  });
                  setListLoad(cuid());
                }}
                className={clsx(
                  queryOptions.status === CommentStatus.inactive
                    ? 'border-indigo-600 text-indigo-600'
                    : null,
                  'p-2 border-b-2 border-transparent text-sm',
                )}
              >
                Trả lời tạm ẩn
              </button>
            </div>
            {listData.total < 0 ? null : listData.total > 0 ? (
              <div className="flex flex-col space-y-2">
                <div className="overflow-x-auto whitespace-nowrap">
                  <div className=" min-w-[1050px] flex justify-between text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <div className="px-0 py-4 text-left w-[300px] sm:w-[300px] lg:w-[300px] xl:w-[400px] 2xl:w-[500px]">
                      Trả lời
                    </div>
                    <div className="px-4 py-4 text-left w-[300px] sm:w-[300px] lg:w-[300px] xl:w-[400px] 2xl:w-[500px]">
                      Trả lời cho câu hỏi
                    </div>
                    <div className="w-[100px] px-2 py-4">Trạng thái</div>
                    <div className="min-w-[180x] flex-1 px-2 py-4"></div>
                  </div>
                  <div className="min-w-[1050px] flex flex-col space-y-3 whitespace-normal">
                    {listData.list.map((item, key: number) => (
                      <ItemReply
                        props={item}
                        target={listTarget[item.pageTargetId]}
                        action={handleAction}
                        key={key}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center flex-col-reverse lg:flex-row">
                  <div className="flex-1 basis-1/6 justify-start text-xs text-gray-500 hidden lg:flex"></div>
                  <div className="flex flex-1 basis-4/6 justify-center">
                    <ReactPaginate
                      pageCount={_.ceil(listData.total / listData.limit)}
                      containerClassName="flex justify-center space-x-2 mt-2"
                      pageLinkClassName="bg-white text-gray-500 hover:bg-gray-50 w-8 h-8 inline-flex justify-center items-center text-xs shadow-xs rounded-full"
                      activeLinkClassName="!bg-indigo-600 !text-gray-50 pointer-events-none"
                      breakLinkClassName="pointer-events-none bg-white text-gray-500 hover:bg-gray-50 w-8 h-8 inline-flex justify-center items-center text-xs shadow-xs rounded-full"
                      nextClassName="hidden"
                      previousClassName="hidden"
                      forcePage={listData.page - 1}
                      onPageChange={page => {
                        setQueryOptions(draft => {
                          draft.page = page.selected + 1;
                        });
                        setListLoad(cuid());
                      }}
                    />
                  </div>
                  <div className="flex flex-1 basis-1/6 justify-end text-xs text-gray-500">
                    Tổng {listData.total} trả lời
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col py-12">
                <div className="text-center">
                  <AnnotationIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-md font-medium text-gray-900">
                    Chưa có trả lời nào
                  </h3>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PopupQuestionReply page_type="question" />
      <ListReplyQuestionDeleteConfirm
        payload={showDeleteItem}
        modal={setShowDeleteItem}
        action={handleDeleteItem}
      />
    </>
  );
};

export default List;
